import type { IDictionaryCountry } from '@/creditflowresearch/domain/vo/IDictionary';

export const getCountryIdsByRegions = (countries: IDictionaryCountry[], regionIds: Set<string>): string[] =>
  countries.filter((c) => regionIds.has(c.regionId)).map((c) => c.id);

const buildCountryLookup = (countries: IDictionaryCountry[]): Map<string, IDictionaryCountry> =>
  new Map(countries.map((c) => [c.id, c]));

const removeCountriesFromExcludedRegions = (
  selectedIds: string[],
  includedRegions: Set<string>,
  countryLookup: Map<string, IDictionaryCountry>,
): string[] =>
  selectedIds.filter((id) => {
    const country = countryLookup.get(id);
    return !country || includedRegions.has(country.regionId);
  });

const findRegionsWithoutSelection = (
  keptCountryIds: string[],
  allRegionIds: string[],
  countryLookup: Map<string, IDictionaryCountry>,
): string[] => {
  const representedRegions = new Set(
    keptCountryIds.map((id) => countryLookup.get(id)?.regionId).filter(Boolean),
  );
  return allRegionIds.filter((id) => !representedRegions.has(id));
};

const hasSelectionChanged = (current: string[], next: string[]): boolean => {
  if (current.length !== next.length) return true;
  const currentSet = new Set(current);
  return next.some((id) => !currentSet.has(id));
};

interface ISyncSelectionWithRegionsParams {
  currentSelected: string[],
  activeRegionIds: string[],
  countries: IDictionaryCountry[],
}

export const syncSelectionWithRegions = ({
  currentSelected,
  activeRegionIds,
  countries,
}: ISyncSelectionWithRegionsParams): string[] | null => {
  if (currentSelected.length === 0) {
    if (activeRegionIds.length === 0) return null;
    return getCountryIdsByRegions(countries, new Set(activeRegionIds));
  }

  const includedRegions = new Set(activeRegionIds);
  const countryLookup = buildCountryLookup(countries);

  const keptCountries = removeCountriesFromExcludedRegions(currentSelected, includedRegions, countryLookup);
  const unrepresentedRegions = findRegionsWithoutSelection(keptCountries, activeRegionIds, countryLookup);
  const expandedCountries = getCountryIdsByRegions(countries, new Set(unrepresentedRegions));

  const nextSelected = [...keptCountries, ...expandedCountries];

  if (!hasSelectionChanged(currentSelected, nextSelected)) return null;

  return nextSelected;
};
